import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bell, X, Check, CheckCheck } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import { Link } from "react-router-dom";

const KIND_COLOR = {
  info: "#3B82F6",
  automation: "#D4AF37",
  alert: "#EF4444",
  success: "#22C55E",
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [wsState, setWsState] = useState("idle"); // idle | connecting | open | closed
  const ref = useRef(null);
  const wsRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch (err) {
      console.debug("notifications refresh failed", err);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Open WebSocket for live notifications
  useEffect(() => {
    const token = localStorage.getItem("nexus_token");
    if (!token || !BACKEND_URL) return;
    let cancelled = false;
    let retryTimer = null;

    const connect = () => {
      if (cancelled) return;
      const base = (BACKEND_URL || "").replace(/\/+$/, "").replace(/^http/, "ws");
      const wsUrl = `${base}/api/ws/notifications?token=${encodeURIComponent(token)}`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        setWsState("connecting");
        ws.onopen = () => setWsState("open");
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg?.type === "ping") {
              // Server keepalive — reply with pong so the read loop on the server resets
              try { ws.send(JSON.stringify({ type: "pong" })); } catch (e) { /* socket closed mid-flight */ }
              return;
            }
            if (msg?.type === "notification" && msg.data) {
              setItems((prev) => [msg.data, ...prev].slice(0, 50));
              setUnread((u) => u + 1);
            }
          } catch (err) {
            console.debug("notifications ws message parse failed", err);
          }
        };
        ws.onclose = () => {
          setWsState("closed");
          if (!cancelled) {
            retryTimer = setTimeout(connect, 4000);
          }
        };
        ws.onerror = () => { try { ws.close(); } catch (e) { /* already closed */ } };
      } catch (err) {
        console.debug("notifications ws connect failed", err);
        retryTimer = setTimeout(connect, 4000);
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* already closed */ } }
    };
  }, []);

  useEffect(() => {
    const click = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  };
  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };
  const remove = async (id) => {
    await api.delete(`/notifications/${id}`);
    setItems((prev) => prev.filter((n) => n.id !== id));
    refresh();
  };

  const liveDot = wsState === "open"
    ? { bg: "bg-emerald-400", label: "LIVE" }
    : wsState === "connecting"
    ? { bg: "bg-amber-400", label: "SYNC" }
    : { bg: "bg-slate-600", label: "OFFLINE" };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notifications-btn"
        className="p-2 text-slate-400 hover:text-[#D4AF37] transition-colors relative"
      >
        <Bell className="w-4 h-4" strokeWidth={1.5} />
        {unread > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-1 text-[10px] font-mono flex items-center justify-center"
            style={{ background: "var(--gold-500)", color: "#050B14" }}
            data-testid="notifications-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] bg-[#0A111F] border border-white/10 z-50 shadow-2xl"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div>
              <div className="flex items-center gap-2">
                <div className="label-eyebrow">// Notifications</div>
                <span className="flex items-center gap-1 font-mono text-[9px] tracking-widest text-slate-500">
                  <span className={`w-1.5 h-1.5 rounded-full ${liveDot.bg}`}></span>
                  {liveDot.label}
                </span>
              </div>
              <div className="font-display text-base text-white mt-0.5">{unread} unread</div>
            </div>
            <button
              onClick={markAll}
              className="text-xs font-mono text-slate-400 hover:text-[#D4AF37] tracking-widest flex items-center gap-1"
            >
              <CheckCheck className="w-3 h-3" /> MARK ALL
            </button>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-xs font-mono">No notifications</div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  className={`group p-4 border-b border-white/5 hover:bg-[#10182B] transition-colors ${
                    n.read ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 mt-2 shrink-0" style={{ background: KIND_COLOR[n.kind] || "#94A3B8" }}></span>
                    <div className="flex-1 min-w-0">
                      {n.link ? (
                        <Link to={n.link} onClick={() => setOpen(false)} className="block">
                          <div className="text-sm text-white">{n.title}</div>
                          <div className="text-xs text-slate-400 mt-1">{n.body}</div>
                        </Link>
                      ) : (
                        <>
                          <div className="text-sm text-white">{n.title}</div>
                          <div className="text-xs text-slate-400 mt-1">{n.body}</div>
                        </>
                      )}
                      <div className="font-mono text-[10px] text-slate-600 mt-2 tracking-widest uppercase">{timeAgo(n.created_at)}</div>
                    </div>
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!n.read && (
                        <button onClick={() => markRead(n.id)} className="text-slate-500 hover:text-[#D4AF37]">
                          <Check className="w-3 h-3" />
                        </button>
                      )}
                      <button onClick={() => remove(n.id)} className="text-slate-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
